import { useState, useEffect, useMemo, useCallback } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "../utils/dayjsConfig";
import { Input, Form, FormInstance, message } from "antd";
import { useWatch } from "antd/es/form/Form";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { TimeView } from "@mui/x-date-pickers/models";
import "./CustomTimePicker.scss";
import { IconClock } from "@tabler/icons-react";
import {
  combineDateAndTime,
  getDefaultSelectableTime,
  getNow,
  getTimePickerBounds,
  isToday,
  MIN_DATETIME_GAP_MINUTES,
  type DateTimeRangeBounds,
} from "../utils/DateFn";

export type CustomTimePickerProps = {
  disabled?: boolean;
  name: string;
  label: string;
  required: boolean;
  form: FormInstance;
  dateFieldName?: string;
  /** If the paired date is empty, copy this date field (e.g. sales open date for close time). */
  fallbackDateField?: string;
  bounds?: DateTimeRangeBounds;
  minGapFrom?: {
    dateField: string;
    timeField: string;
    gapMinutes?: number;
  };
  validatePast?: boolean;
};

const CustomTimePicker = ({
  disabled,
  name,
  label,
  required,
  form,
  dateFieldName,
  fallbackDateField,
  bounds,
  minGapFrom,
  validatePast = true,
}: CustomTimePickerProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Dayjs | null>(null);

  const fieldVal = useWatch(name, form);
  const pairedDate = useWatch(dateFieldName ?? "", form);
  const gapStartDate = useWatch(minGapFrom?.dateField ?? "", form);
  const gapStartTime = useWatch(minGapFrom?.timeField ?? "", form);

  const referenceDate = useMemo(() => {
    if (pairedDate && dayjs(pairedDate).isValid()) {
      return dayjs(pairedDate).startOf("day");
    }
    return getNow().startOf("day");
  }, [pairedDate]);

  const isSelectedToday = useMemo(
    () => isToday(pairedDate),
    [pairedDate],
  );

  const { minTime, maxTime } = useMemo(() => {
    const date = pairedDate ? dayjs(pairedDate) : null;
    return getTimePickerBounds({
      pairedDate: date?.isValid() ? date : null,
      minDateTime: bounds?.minDateTime,
      maxDateTime: bounds?.maxDateTime,
      savedStartDateTime: bounds?.savedStartDateTime,
      enforceNowOnToday: !bounds?.isEdit,
      minFrom: minGapFrom
        ? {
            date: gapStartDate ? dayjs(gapStartDate) : null,
            time: gapStartTime,
            gapMinutes:
              minGapFrom.gapMinutes ?? MIN_DATETIME_GAP_MINUTES,
          }
        : undefined,
    });
  }, [
    pairedDate,
    bounds?.minDateTime,
    bounds?.maxDateTime,
    bounds?.savedStartDateTime,
    bounds?.isEdit,
    minGapFrom,
    gapStartDate,
    gapStartTime,
  ]);

  useEffect(() => {
    const parsed = dayjs(fieldVal, ["hh:mm A", "HH:mm:ss"], true);
    if (parsed.isValid()) {
      const onRefDay = referenceDate
        .hour(parsed.hour())
        .minute(parsed.minute());
      setValue(onRefDay);
      if (
        typeof fieldVal === "string" &&
        fieldVal !== parsed.format("hh:mm A")
      ) {
        form.setFieldValue(name, parsed.format("hh:mm A"));
      }
    } else if (!fieldVal) {
      setValue(null);
    }
  }, [fieldVal, form, name, referenceDate]);

  const effectiveMinTime = useMemo(() => {
    const nowOnRef = referenceDate
      .hour(getNow().hour())
      .minute(getNow().minute());
    if (!minTime) {
      return validatePast && isSelectedToday ? nowOnRef : undefined;
    }
    if (validatePast && isSelectedToday && nowOnRef.isAfter(minTime)) {
      return nowOnRef;
    }
    return minTime;
  }, [minTime, validatePast, isSelectedToday, referenceDate]);

  const useDisablePast = useMemo(
    () =>
      validatePast &&
      isSelectedToday &&
      (!effectiveMinTime || !effectiveMinTime.isAfter(getNow())),
    [validatePast, isSelectedToday, effectiveMinTime],
  );

  const clampTime = useCallback(
    (next: Dayjs): Dayjs => {
      let result = next.second(0).millisecond(0);

      if (effectiveMinTime && result.isBefore(effectiveMinTime)) {
        result = effectiveMinTime;
      }
      if (maxTime && result.isAfter(maxTime)) {
        result = maxTime;
      }

      return result;
    },
    [effectiveMinTime, maxTime],
  );

  const shouldDisableTime = useCallback(
    (timeValue: Dayjs, view: TimeView) => {
      if (view !== "hours" && view !== "minutes") return false;

      const hour = timeValue.hour();

      if (view === "hours") {
        const endOfHour = referenceDate.hour(hour).minute(59);
        const startOfHour = referenceDate.hour(hour).minute(0);
        if (effectiveMinTime && endOfHour.isBefore(effectiveMinTime)) {
          return true;
        }
        if (maxTime && startOfHour.isAfter(maxTime)) {
          return true;
        }
        return false;
      }

      const candidate = referenceDate.hour(hour).minute(timeValue.minute());
      if (effectiveMinTime && candidate.isBefore(effectiveMinTime)) {
        return true;
      }
      if (maxTime && candidate.isAfter(maxTime)) {
        return true;
      }
      return false;
    },
    [referenceDate, effectiveMinTime, maxTime],
  );

  const openPicker = () => {
    if (disabled) return;

    let paired = dateFieldName ? form.getFieldValue(dateFieldName) : null;
    if (!paired && dateFieldName && fallbackDateField) {
      const fallback = form.getFieldValue(fallbackDateField);
      if (fallback) {
        form.setFieldValue(dateFieldName, fallback);
        paired = fallback;
      }
    }

    if (dateFieldName && !paired) {
      message.warning(
        fallbackDateField
          ? "Please set the sales open date first"
          : "Please select a date first",
      );
      return;
    }

    const refDay =
      paired && dayjs(paired).isValid()
        ? dayjs(paired).startOf("day")
        : referenceDate;

    const parsed = dayjs(fieldVal, ["hh:mm A", "HH:mm:ss"], true);
    if (!parsed.isValid()) {
      const initial = clampTime(
        getDefaultSelectableTime(refDay, { minTime, maxTime }),
      );
      setValue(initial);
      form.setFieldValue(name, initial.format("hh:mm A"));
    } else {
      setValue(refDay.hour(parsed.hour()).minute(parsed.minute()));
    }

    setOpen(true);
  };

  const handleAccept = (newValue: Dayjs | null) => {
    if (newValue) {
      const next = clampTime(newValue);
      setValue(next);
      form.setFieldValue(name, next.format("hh:mm A"));
      form.validateFields([name]).catch(() => undefined);
    }
    setOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Form.Item
        name={name}
        label={label}
        dependencies={
          dateFieldName
            ? [
                dateFieldName,
                ...(minGapFrom
                  ? [minGapFrom.dateField, minGapFrom.timeField]
                  : []),
              ]
            : undefined
        }
        rules={[
          {
            required,
            message: "Please select time",
          },
          {
            validator: async (_, timeVal) => {
              if (!timeVal || !dateFieldName) return Promise.resolve();

              const dateVal = form.getFieldValue(dateFieldName);
              if (!dateVal) return Promise.resolve();

              const combined = combineDateAndTime(dateVal, timeVal);
              if (!combined) return Promise.resolve();

              const now = getNow();
              const savedStart = bounds?.savedStartDateTime;

              if (
                bounds?.isEdit &&
                savedStart?.isValid() &&
                combined.isBefore(savedStart)
              ) {
                return Promise.reject(
                  new Error(
                    `Time cannot be before the saved event start (${savedStart.format("hh:mm A")})`,
                  ),
                );
              }

              if (validatePast && combined.isBefore(now)) {
                return Promise.reject(
                  new Error("Time cannot be in the past for the selected date"),
                );
              }

              if (
                bounds?.minDateTime &&
                !bounds?.isEdit &&
                combined.isBefore(bounds.minDateTime)
              ) {
                return Promise.reject(
                  new Error(
                    `Time cannot be before ${bounds.minDateTime.format("hh:mm A")}`,
                  ),
                );
              }

              if (
                bounds?.maxDateTime &&
                combined.isAfter(bounds.maxDateTime)
              ) {
                return Promise.reject(
                  new Error(
                    `Time cannot be after event end (${bounds.maxDateTime.format("hh:mm A")})`,
                  ),
                );
              }

              if (minGapFrom) {
                const start = combineDateAndTime(
                  form.getFieldValue(minGapFrom.dateField),
                  form.getFieldValue(minGapFrom.timeField),
                );
                const gap =
                  minGapFrom.gapMinutes ?? MIN_DATETIME_GAP_MINUTES;
                if (
                  start &&
                  dayjs(dateVal).format("YYYY-MM-DD") ===
                    start.format("YYYY-MM-DD")
                ) {
                  if (!combined.isAfter(start)) {
                    return Promise.reject(
                      new Error(
                        gap === 0
                          ? "Sales close time must be after sales open time"
                          : "Close time must be after open time",
                      ),
                    );
                  }
                  if (gap > 0 && combined.diff(start, "minute") < gap) {
                    return Promise.reject(
                      new Error(
                        `Must be at least ${gap} minutes after sales open`,
                      ),
                    );
                  }
                }
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          readOnly
          disabled={disabled}
          onClick={openPicker}
          placeholder="Select time"
          suffix={<IconClock size={14} color="rgba(65,70,81,0.25)" />}
        />
      </Form.Item>

      <MobileTimePicker
        value={value}
        referenceDate={referenceDate}
        disablePast={useDisablePast}
        shouldDisableTime={shouldDisableTime}
        minutesStep={5}
        onChange={(newValue) => {
          if (newValue) {
            const next = clampTime(newValue);
            setValue(next);
          }
        }}
        onAccept={handleAccept}
        onClose={() => setOpen(false)}
        open={open}
        slotProps={{
          textField: {
            style: { display: "none" },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomTimePicker;
